/**
 * @description Service layer for handling authentication API calls and managing client side session persistence.
 */

import api from '../utils/axios';
import axios from 'axios';
import { API_URL } from '../utils/axios';
import { clearAccessToken, getAccessToken, setAccessToken } from '../utils/accessTokenStore';
import { LoginRequest, RegisterRequest, RegistrationResult ,AuthResponse, User } from '../types/auth.types';

function parseJwt (token: string) {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        return null;
    }
}

function normalizeRole(rawRole: unknown): User["role"] | null {
    if (typeof rawRole === "string") {
        const normalized = rawRole.trim().toUpperCase();
        if (normalized === "ADMIN" || normalized === "ROLE_ADMIN") {
            return "ADMIN";
        }
        if (normalized === "USER" || normalized === "ROLE_USER") {
            return "USER";
        }
        return null;
    }

    if (Array.isArray(rawRole)) {
        const normalizedRoles = rawRole
            .map((entry) => normalizeRole(entry))
            .filter((entry): entry is User["role"] => entry !== null);
        if (normalizedRoles.includes("ADMIN")) {
            return "ADMIN";
        }
        if (normalizedRoles.includes("USER")) {
            return "USER";
        }
    }

    return null;
}

type PasswordCredentialCtor = new (data: {
  id: string;
  password: string;
  name?: string;
}) => unknown;

const tryStoreBrowserCredential = async (
  email: string,
  password: string,
  displayName?: string
) => {
  if (
    typeof window === "undefined" ||
    !window.isSecureContext ||
    typeof navigator === "undefined"
  ) {
    return;
  }

  const passwordCredential = (window as Window & {
    PasswordCredential?: PasswordCredentialCtor;
  }).PasswordCredential;

  const credentialsApi = (navigator as Navigator & {
    credentials?: { store?: (credential: unknown) => Promise<unknown> };
  }).credentials;

  if (!passwordCredential || !credentialsApi?.store) {
    return;
  }

  try {
    const credential = new passwordCredential({
      id: email,
      password,
      name: displayName && displayName.trim() ? displayName.trim() : email,
    });
    await credentialsApi.store(credential);
  } catch {
    // Ignore unsupported browser/permission failures.
  }
};

export const authService = {


  async register(data: RegisterRequest): Promise<RegistrationResult> {
    const response = await api.post<RegistrationResult>('/auth/register', data);
    return response.data;
  },

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', data);

    if (response.data.accessToken) {
      setAccessToken(response.data.accessToken);
      localStorage.removeItem('refreshToken');
      localStorage.setItem('user', JSON.stringify(response.data.user));
      void tryStoreBrowserCredential(
        data.email,
        data.password,
        response.data.user?.username
      );
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("auth-changed"));
    }
    return response.data;
  },

  logout() {
    const performLogout = async () => {
      try {
        await api.post('/auth/logout');
      } finally {
        clearAccessToken();
        localStorage.clear();
        if (typeof window !== "undefined") {
          window.dispatchEvent(new Event("auth-changed"));
        }
        window.location.href = '/';
      }
    };
    void performLogout();
  },

  async restoreSession(): Promise<User | null> {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      clearAccessToken();
      localStorage.removeItem("refreshToken");
      return null;
    }

    try {
      const response = await axios.post<AuthResponse>(
        `${API_URL}/auth/refresh-token`,
        {},
        { withCredentials: true }
      );
      const accessToken = response.data?.accessToken;
      if (!accessToken) {
        clearAccessToken();
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        return null;
      }
      setAccessToken(accessToken);
      localStorage.removeItem('refreshToken');
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return response.data.user;
      }
      return this.getCurrentUser();
    } catch {
      clearAccessToken();
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      return null;
    }
  },

  isAuthenticated(): boolean {
    const token = getAccessToken();
    if (!token) return false;
    const decoded = parseJwt(token);
    if (decoded && decoded.exp * 1000 < Date.now()) {
        clearAccessToken();
        return false;
    }
    return true;
  },

  getCurrentUser(): User | null {
    const token = getAccessToken();
    const userStr = localStorage.getItem('user');

    if (!token) return null;

    const decodedToken = parseJwt(token);
    if (!decodedToken) return null;

    let storedUser: Partial<User> = {};
    if (userStr) {
        try {
            storedUser = JSON.parse(userStr);
        } catch(e) {
            //ignore
        }
    }

    const roleFromToken = normalizeRole(decodedToken.role ?? decodedToken.roles ?? decodedToken.authorities);
    const roleFromStoredUser = normalizeRole(storedUser.role);

    return {
        ...storedUser,
        id: decodedToken.sub || decodedToken.id,
        role: roleFromToken ?? roleFromStoredUser ?? "USER",
    } as User;
  }

};
